import axios, { AxiosError } from 'axios';
import type {
    AxiosInstance,
    AxiosRequestConfig,
    AxiosResponse,
    InternalAxiosRequestConfig,
} from 'axios';
import { insufficientCreditsEmitter } from '@src/shared/services/insufficient-credits-emitter';

// Token management keys
const TOKEN_KEY = 'auth_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

/**
 * Token utility functions - for use by infrastructure and application layers only
 * Presentation layer should use AuthService methods instead
 */
export const authTokens = {
    getAccessToken: (): string | null => localStorage.getItem(TOKEN_KEY),
    getRefreshToken: (): string | null => localStorage.getItem(REFRESH_TOKEN_KEY),
    setAccessToken: (token: string): void => localStorage.setItem(TOKEN_KEY, token),
    setRefreshToken: (token: string): void => localStorage.setItem(REFRESH_TOKEN_KEY, token),
    clear: (): void => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
    },
    hasAccessToken: (): boolean => !!localStorage.getItem(TOKEN_KEY),
    hasRefreshToken: (): boolean => !!localStorage.getItem(REFRESH_TOKEN_KEY),
    hasTokens: (): boolean => !!localStorage.getItem(TOKEN_KEY) && !!localStorage.getItem(REFRESH_TOKEN_KEY),
};

/**
 * Extract error message from API error response
 * Handles various error formats from the backend
 */
function extractErrorMessage(error: AxiosError): string {
    // Handle timeout errors
    if (error.code === 'ECONNABORTED') {
        return 'Request timeout. Please check your connection and try again.';
    }

    // Handle network errors
    if (!error.response) {
        return 'Network error. Please check your internet connection.';
    }

    // Check if response has data
    const responseData = error.response?.data as any;

    if (responseData) {
        // Format 1: { detail: { message: "...", error: "..." } }
        if (responseData.detail?.message) {
            return responseData.detail.message;
        }

        // Format 2: { detail: "..." }
        if (typeof responseData.detail === 'string') {
            return responseData.detail;
        }

        // Format 3: { message: "..." }
        if (responseData.message) {
            return responseData.message;
        }

        // Format 4: { error: "..." }
        if (responseData.error) {
            return typeof responseData.error === 'string'
                ? responseData.error
                : JSON.stringify(responseData.error);
        }
    }

    // Fallback to axios error message
    return error.message || 'An unexpected error occurred';
}

export interface ApiClientConfig {
    baseURL: string;
    timeout?: number;
    withCredentials?: boolean;
}

export interface UploadFileOptions {
    file: File;
    additionalData?: Record<string, any>;
    onUploadProgress?: (progressEvent: any) => void;
}

/**
 * API Client class for making HTTP requests with authentication
 * and token refresh capabilities
 */
export class ApiClient {
    private client: AxiosInstance;
    private isRefreshing = false;
    private failedRequestsQueue: Array<{
        resolve: (token: string) => void;
        reject: (error: AxiosError) => void;
    }> = [];

    constructor(config: ApiClientConfig) {
        this.client = axios.create({
            baseURL: config.baseURL,
            timeout: config.timeout || 30000, // 30 seconds default
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            withCredentials: config.withCredentials || false,
        });

        this.setupInterceptors();
    }

    /**
     * Get authentication token from storage
     */
    private getAuthToken(): string | null {
        const token = localStorage.getItem(TOKEN_KEY);
        if (import.meta.env.DEV && !token) {
            console.warn('⚠️ No auth token found in storage');
        }
        return token;
    }

    /**
     * Get refresh token from storage
     */
    private getRefreshToken(): string | null {
        return localStorage.getItem(REFRESH_TOKEN_KEY);
    }

    /**
     * Set authentication token in storage
     */
    public setAuthToken(token: string): void {
        if (import.meta.env.DEV) {
            console.log('💾 Setting auth token:', token.substring(0, 10) + '...');
        }
        localStorage.setItem(TOKEN_KEY, token);
    }

    /**
     * Set refresh token in storage
     */
    public setRefreshToken(token: string): void {
        localStorage.setItem(REFRESH_TOKEN_KEY, token);
    }

    /**
     * Clear all authentication tokens
     */
    public clearAuthTokens(): void {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);
    }

    /**
     * Process queued requests after token refresh
     */
    private processQueue(error: AxiosError | null, token: string | null = null): void {
        this.failedRequestsQueue.forEach((promise) => {
            if (error) {
                promise.reject(error);
            } else {
                promise.resolve(token!);
            }
        });

        this.failedRequestsQueue = [];
    }

    /**
     * Setup request and response interceptors
     */
    private setupInterceptors(): void {
        // Request Interceptor
        this.client.interceptors.request.use(
            (config: InternalAxiosRequestConfig) => {
                // Add authorization token if available
                const token = this.getAuthToken();
                if (token && config.headers) {
                    config.headers.Authorization = `Bearer ${token}`;
                }

                // Add request timestamp for monitoring
                config.headers['X-Request-ID'] = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

                // Add client version if available
                if (import.meta.env.VITE_APP_VERSION) {
                    config.headers['X-Client-Version'] = import.meta.env.VITE_APP_VERSION;
                }

                // Log request in development
                if (import.meta.env.DEV) {
                    console.log('📤 API Request:', {
                        method: config.method?.toUpperCase(),
                        url: config.url,
                        baseURL: config.baseURL,
                        params: config.params,
                        data: config.data,
                    });
                }

                return config;
            },
            (error: AxiosError) => {
                if (import.meta.env.DEV) {
                    console.error('❌ Request Error:', error);
                }
                return Promise.reject(error);
            }
        );

        // Response Interceptor
        this.client.interceptors.response.use(
            (response: AxiosResponse) => {
                if (import.meta.env.DEV) {
                    console.log('📥 API Response:', {
                        status: response.status,
                        url: response.config.url,
                        data: response.data,
                    });
                }

                // Auto-store tokens from auth responses (login, OTP verification, etc.)
                const isAuthResponse = response.config.url?.includes('/auth/verify-otp') ||
                    response.config.url?.includes('/auth/login');
                
                if (isAuthResponse) {
                    if (import.meta.env.DEV) {
                        console.log('🔍 Checking for tokens in auth response:', {
                            url: response.config.url,
                            hasData: !!response.data,
                            hasTokens: !!response.data?.tokens,
                            keys: response.data ? Object.keys(response.data) : []
                        });
                    }

                    if (response.data?.tokens) {
                        const { access_token, refresh_token } = response.data.tokens;
                        if (access_token) {
                            this.setAuthToken(access_token);
                            if (import.meta.env.DEV) {
                                console.log('🔑 Auth tokens stored from response');
                            }
                        }
                        if (refresh_token) {
                            this.setRefreshToken(refresh_token);
                        }
                    } else if (response.data?.data?.tokens) {
                        // Handle wrapped response format { data: { tokens: ... } }
                        const { access_token, refresh_token } = response.data.data.tokens;
                        if (access_token) {
                            this.setAuthToken(access_token);
                            if (import.meta.env.DEV) {
                                console.log('🔑 Auth tokens stored from wrapped response');
                            }
                        }
                        if (refresh_token) {
                            this.setRefreshToken(refresh_token);
                        }
                    } else {
                        if (import.meta.env.DEV) {
                            console.warn('⚠️ Auth response missing tokens property:', response.data);
                        }
                    }
                }

                return response;
            },
            async (error: AxiosError) => {
                const originalRequest = error.config as InternalAxiosRequestConfig & {
                    _retry?: boolean;
                };

                if (import.meta.env.DEV) {
                    console.error('❌ Response Error:', {
                        status: error.response?.status,
                        url: error.config?.url,
                        message: error.message,
                        data: error.response?.data,
                    });
                }

                // Check if this is an auth endpoint (login, register, refresh)
                const isAuthEndpoint = originalRequest.url?.includes('/login') ||
                    originalRequest.url?.includes('/register') ||
                    originalRequest.url?.includes('/refresh');

                // Handle 401 Unauthorized - Token refresh logic
                // Skip token refresh for auth endpoints to prevent infinite loops and show proper errors
                if (error.response?.status === 401 && !originalRequest._retry && !isAuthEndpoint) {
                    if (this.isRefreshing) {
                        if (import.meta.env.DEV) {
                            console.log('🔄 Token refresh in progress, queuing request:', originalRequest.url);
                        }
                        return new Promise((resolve, reject) => {
                            this.failedRequestsQueue.push({ resolve, reject });
                        })
                            .then((token) => {
                                if (originalRequest.headers) {
                                    originalRequest.headers.Authorization = `Bearer ${token}`;
                                }
                                if (import.meta.env.DEV) {
                                    console.log('✅ Retrying queued request with new token:', originalRequest.url);
                                }
                                return this.client(originalRequest);
                            })
                            .catch((err) => {
                                return Promise.reject(err);
                            });
                    }

                    originalRequest._retry = true;
                    this.isRefreshing = true;

                    const refreshToken = this.getRefreshToken();

                    if (!refreshToken) {
                        if (import.meta.env.DEV) {
                            console.warn('⚠️ No refresh token available, redirecting to login');
                        }
                        this.clearAuthTokens();
                        this.processQueue(error, null);
                        window.location.href = '/auth';
                        return Promise.reject(error);
                    }

                    if (import.meta.env.DEV) {
                        console.log('🔄 Starting token refresh...');
                    }

                    try {
                        const response = await axios.post(`${this.client.defaults.baseURL}/auth/refresh`, {
                            refresh_token: refreshToken,
                        });

                        const { access_token: newToken, refresh_token: newRefreshToken } = response.data;

                        if (import.meta.env.DEV) {
                            console.log('✅ Token refresh successful');
                        }

                        this.setAuthToken(newToken);
                        if (newRefreshToken) {
                            this.setRefreshToken(newRefreshToken);
                        }

                        if (originalRequest.headers) {
                            originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        }

                        this.processQueue(null, newToken);
                        return this.client(originalRequest);
                    } catch (refreshError) {
                        if (import.meta.env.DEV) {
                            console.error('❌ Token refresh failed:', refreshError);
                        }
                        this.processQueue(refreshError as AxiosError, null);
                        this.clearAuthTokens();
                        window.location.href = '/auth';
                        return Promise.reject(refreshError);
                    } finally {
                        this.isRefreshing = false;
                    }
                }

                // Handle 402 Payment Required - Insufficient Credits
                if (error.response?.status === 402) {
                    let responseData = error.response?.data as any;

                    // If response is a Blob (from downloadBlob), convert it to JSON
                    if (responseData instanceof Blob) {
                        try {
                            const text = await responseData.text();
                            responseData = JSON.parse(text);
                        } catch (parseError) {
                            console.error('Failed to parse 402 blob response:', parseError);
                        }
                    }

                    if (import.meta.env.DEV) {
                        console.warn('⚠️ Payment Required (402):', responseData);
                    }

                    // Extract data from the response
                    const message = responseData?.message || 'Insufficient credits. Please top up to continue.';

                    // Try to extract top-up URL if present in the message
                    let topUpUrl: string | undefined;
                    const urlMatch = message.match(/(https?:\/\/[^\s]+)/);
                    if (urlMatch) {
                        topUpUrl = urlMatch[1];
                    } else {
                        // Default top-up URL
                        topUpUrl = '/credits/top-up';
                    }

                    // Emit event to show the modal
                    insufficientCreditsEmitter.emit({
                        message,
                        topUpUrl,
                    });
                }

                // Handle other error codes
                if (error.response?.status === 403) {
                    console.error('Access forbidden - insufficient permissions');
                }
                if (error.response?.status === 404) {
                    console.error('Resource not found');
                }
                if (error.response?.status === 500) {
                    console.error('Server error - please try again later');
                }

                // Handle timeout errors
                if (error.code === 'ECONNABORTED') {
                    console.error('Request timeout - the server took too long to respond');
                }

                // Handle network errors
                if (!error.response && error.code !== 'ECONNABORTED') {
                    console.error('Network error - please check your connection');
                }

                return Promise.reject(error);
            }
        );
    }

    /**
     * Make a generic API request
     */
    public async request<T = any>(config: AxiosRequestConfig): Promise<T> {
        try {
            const response = await this.client.request<T>(config);
            return response.data;
        } catch (error) {
            // Transform the error to include a user-friendly message
            if (axios.isAxiosError(error)) {
                const errorMessage = extractErrorMessage(error);

                // Create a new error with the extracted message
                const transformedError = new Error(errorMessage) as Error & {
                    response?: typeof error.response;
                    status?: number;
                    code?: string;
                };

                transformedError.response = error.response;
                transformedError.status = error.response?.status;
                transformedError.code = error.code;

                throw transformedError;
            }

            throw error;
        }
    }

    /**
     * GET request
     */
    public get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({ ...config, method: 'GET', url });
    }

    /**
     * POST request
     */
    public post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({ ...config, method: 'POST', url, data });
    }

    /**
     * PUT request
     */
    public put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({ ...config, method: 'PUT', url, data });
    }

    /**
     * PATCH request
     */
    public patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({ ...config, method: 'PATCH', url, data });
    }

    /**
     * DELETE request
     */
    public delete<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
        return this.request<T>({ ...config, method: 'DELETE', url, data });
    }

    /**
     * Upload file with additional form data and progress tracking
     */
    public async uploadFile<T = any>(
        url: string,
        options: UploadFileOptions
    ): Promise<T> {
        const formData = new FormData();

        // Append the file
        formData.append('file', options.file);

        // Append additional data if provided
        if (options.additionalData) {
            Object.entries(options.additionalData).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    // Handle different types of values
                    if (value instanceof File || value instanceof Blob) {
                        formData.append(key, value);
                    } else if (typeof value === 'object') {
                        formData.append(key, JSON.stringify(value));
                    } else {
                        formData.append(key, String(value));
                    }
                }
            });
        }

        return this.request<T>({
            method: 'POST',
            url,
            data: formData,
            headers: {
                'Content-Type': 'multipart/form-data',
            },
            onUploadProgress: options.onUploadProgress,
        });
    }

    /**
     * Download file as Blob (for binary data like PDFs, images, etc.)
     */
    public async downloadBlob(url: string, config?: AxiosRequestConfig): Promise<Blob> {
        try {
            const response = await this.client.request({
                ...config,
                method: 'GET',
                url,
                responseType: 'blob',
            });
            return response.data;
        } catch (error) {
            // Handle 402 errors that return JSON instead of blob
            if (axios.isAxiosError(error) && error.response?.status === 402) {
                // The response is a blob but contains JSON error data
                const blob = error.response.data;

                if (blob instanceof Blob) {
                    try {
                        // Convert blob to text to read the JSON error
                        const text = await blob.text();
                        const errorData = JSON.parse(text);

                        // The 402 interceptor will handle showing the modal
                        // Just need to extract and throw a proper error
                        const errorMessage = errorData?.detail?.message || 'Insufficient credits. Please top up to continue.';

                        const transformedError = new Error(errorMessage) as Error & {
                            response?: typeof error.response;
                            status?: number;
                            code?: string;
                        };

                        transformedError.response = error.response;
                        transformedError.status = 402;
                        transformedError.code = error.code;

                        throw transformedError;
                    } catch (parseError) {
                        // If parsing fails, fall through to general error handling
                        if (parseError instanceof Error && parseError.message !== 'Insufficient credits. Please top up to continue.') {
                            console.error('Failed to parse 402 blob error:', parseError);
                        } else {
                            throw parseError;
                        }
                    }
                }
            }

            // Transform the error to include a user-friendly message
            if (axios.isAxiosError(error)) {
                const errorMessage = extractErrorMessage(error);

                // Create a new error with the extracted message
                const transformedError = new Error(errorMessage) as Error & {
                    response?: typeof error.response;
                    status?: number;
                    code?: string;
                };

                transformedError.response = error.response;
                transformedError.status = error.response?.status;
                transformedError.code = error.code;

                throw transformedError;
            }

            throw error;
        }
    }

    /**
     * Create cancel token source for request cancellation
     */
    public createCancelToken() {
        return axios.CancelToken.source();
    }

    /**
     * Check if error is a cancel error
     */
    public isCancel(error: any): boolean {
        return axios.isCancel(error);
    }
}

// Export a default instance for backward compatibility
const apiClient = new ApiClient({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    timeout: 30000,
});

export default apiClient;
